import { useEffect, useState } from 'react';
import { redactTextWithGemini, rewriteReportWithGemini } from '../services/geminiService';
import { buildRestoreMap, restorePII, textToDocxBlob } from '../utils/restoreUtils';
import { extractTextFromFile } from '../services/fileParser';
import PostRedactionSaveModal from './PostRedactionSaveModal';

type Step = 'idle' | 'redacting' | 'rewriting' | 'done' | 'error';
const MAX_SMART_INPUT_CHARS = 500000;

interface SmartRedactModeProps {
  onOpenSettings?: () => void;
}

export function SmartRedactMode({ onOpenSettings }: SmartRedactModeProps) {
  const [step, setStep] = useState<Step>('idle');
  const [finalText, setFinalText] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [sourceDisplayName, setSourceDisplayName] = useState('document.txt');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [smartExportBlob, setSmartExportBlob] = useState<Blob | null>(null);
  const [smartOutputExt, setSmartOutputExt] = useState<'txt' | 'doc'>('txt');

  /** After Smart flow finishes, show the same Save / Drive modal as Standard mode (plaintext or .doc blob). */
  useEffect(() => {
    if (step !== 'done') return;
    setSmartExportBlob(null);
    setSmartOutputExt('txt');
    setShowSaveModal(true);
  }, [step]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError('');
      setSourceDisplayName(file.name);
      // Read file as text (TXT direct, PDF/DOCX via existing parser).
      // Scanned PDF OCR: not supported in Smart mode yet — wire extractTextFromFile
      // with onProgress + ocrPageFailures handling here when adding scanned support.
      const fileName = file.name.toLowerCase();
      const isTxt = file.type === 'text/plain' || fileName.endsWith('.txt');
      const originalText = isTxt ? await file.text() : (await extractTextFromFile(file)).text;

      if (!originalText.trim()) {
        throw new Error('No readable text was found in this file. Try a different file format or a clearer source document.');
      }

      if (originalText.length > MAX_SMART_INPUT_CHARS) {
        throw new Error(
          'This document is too large to process. Try splitting it into smaller sections.'
        );
      }

      // Step 1: Redact
      setStep('redacting');
      const redactResult = await redactTextWithGemini(originalText);
      const redactedText = redactResult.redactedText;

      // Build restore map from original vs redacted
      const restoreMap = buildRestoreMap(originalText, redactedText);

      // Step 2: AI writing pass on redacted text (no PII)
      setStep('rewriting');
      const rewrittenText = await rewriteReportWithGemini(redactedText);

      // Step 3: Restore PII into rewritten text
      const restoredText = restorePII(rewrittenText, restoreMap);

      setFinalText(restoredText);
      setStep('done');
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(message);
      setStep('error');
    }
  };

  const openTxtSaveModal = () => {
    setSmartExportBlob(null);
    setSmartOutputExt('txt');
    setShowSaveModal(true);
  };

  const openDocSaveModal = () => {
    setSmartExportBlob(textToDocxBlob(finalText));
    setSmartOutputExt('doc');
    setShowSaveModal(true);
  };

  return (
    <div className="flex flex-col gap-6 p-6 max-w-2xl mx-auto">
      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-medium text-gray-900">Smart Prepare + Rewrite</h2>
        <p className="text-sm text-gray-500">
          Upload a document. Redact AI prepares it by redacting PII, rewrites it as a professional report, then restores
          student details locally when you choose — keeping a clear separation between the sensitive original and
          AI-assisted drafting.
        </p>
      </div>

      {step === 'idle' && (
        <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-10 cursor-pointer hover:border-green-600 transition-colors">
          <span className="text-sm text-gray-500 mb-2">Click to upload a document</span>
          <span className="text-xs text-gray-400">.txt, .docx, .pdf</span>
          <input type="file" className="hidden" accept=".txt,.docx,.pdf" onChange={handleFile} />
        </label>
      )}

      {step === 'redacting' && (
        <div className="flex flex-col items-center gap-3 py-10">
          <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Preparing document (redacting PII)...</p>
        </div>
      )}

      {step === 'rewriting' && (
        <div className="flex flex-col items-center gap-3 py-10">
          <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Rewriting as professional report...</p>
        </div>
      )}

      {step === 'error' && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {step === 'done' && (
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 max-h-96 overflow-y-auto">
            <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{finalText}</pre>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={openTxtSaveModal}
              className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors"
            >
              Save / Download .txt
            </button>
            <button
              type="button"
              onClick={openDocSaveModal}
              className="flex-1 px-4 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors"
            >
              Save / Download .doc
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowSaveModal(true)}
            className="text-sm text-emerald-700 hover:underline text-center"
          >
            Open save options (student code & Google Drive)…
          </button>
          <button
            onClick={() => {
              setStep('idle');
              setFinalText('');
              setShowSaveModal(false);
            }}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            Process another document
          </button>
        </div>
      )}
      {step === 'done' ? (
        <PostRedactionSaveModal
          isOpen={showSaveModal}
          sourceFileName={sourceDisplayName}
          redactedText={finalText}
          uploadBlob={smartExportBlob}
          outputExtension={smartOutputExt}
          onClose={() => setShowSaveModal(false)}
          onOpenSettings={onOpenSettings}
        />
      ) : null}
    </div>
  );
}
